import { AudioLines, FileAudio } from "lucide-react";
import { type JSX, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { FilePlayer } from "@/components/ui/visits/NewVisitUploadV32";
import { useAudioStream } from "@/hooks/useAudioStream";
import { captureElementStream } from "@/lib/mediaRecorder";
import RecorderVisualizer from "./recorder-visualizer";
import { formatTime } from "./utils/format-timer";

interface CustomAudioPlayerProps {
  mediaBlobUrl: string;
  /** The chosen file (an upload): its name and size head the v3.2 card. */
  file?: File;
}

const fileSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** v3.2: the recording (or chosen file) as a card with the v3 player. */
const V3RecordingCard = ({
  mediaBlobUrl,
  file,
}: CustomAudioPlayerProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      className="w-full rounded-v3-panel bg-v3-panel p-4 font-v3 text-v3-text shadow-v3-hairline-strong"
      data-testid="recording-card-v3"
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-v3-surface-2 text-v3-text-2"
        >
          {file ? (
            <FileAudio className="size-5" />
          ) : (
            <AudioLines className="size-5" />
          )}
        </span>
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold">
            {file?.name ?? t("newVisitV32.fallback.recording")}
          </p>
          {file && (
            <p className="text-[13px] text-v3-text-3">{fileSize(file.size)}</p>
          )}
        </div>
      </div>
      <div className="mt-3">
        <FilePlayer url={mediaBlobUrl} />
      </div>
    </div>
  );
};

const RecorderAudioPlayer = (props: CustomAudioPlayerProps): JSX.Element =>
  AppConfig.enableAppRedesign === true ? (
    <V3RecordingCard {...props} />
  ) : (
    <ClassicRecorderAudioPlayer mediaBlobUrl={props.mediaBlobUrl} />
  );

const ClassicRecorderAudioPlayer: React.FC<CustomAudioPlayerProps> = ({
  mediaBlobUrl,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioStream, setAudioStream] = useAudioStream(audioRef, mediaBlobUrl);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, [mediaBlobUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = (): void => {
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    return (): void => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [mediaBlobUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleGetDuration = (event: Event): void => {
      const target = event.target as HTMLAudioElement;
      target.currentTime = 0;
      target.removeEventListener("timeupdate", handleGetDuration);
      setDuration(target.duration);
    };

    const handleLoadedMetadata = (): void => {
      if (audio.duration === Infinity || isNaN(audio.duration)) {
        audio.currentTime = 1e101;
        audio.addEventListener("timeupdate", handleGetDuration);
      } else {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    return (): void => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleGetDuration);
    };
  }, [mediaBlobUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = (): void => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.load();

      const handleLoaded = (): void => {
        // Without captureStream (WebKit) the Web Audio stream from
        // useAudioStream stays connected, so keep it.
        const stream = captureElementStream(audioRef.current);
        if (stream) setAudioStream(stream);
        audio.removeEventListener("loadedmetadata", handleLoaded);
      };
      audio.addEventListener("loadedmetadata", handleLoaded);
    };

    audio.addEventListener("ended", handleEnded);
    return (): void => {
      audio.removeEventListener("ended", handleEnded);
    };
  }, [mediaBlobUrl, setAudioStream]);

  const togglePlayPause = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      if (!audioStream) {
        const stream = captureElementStream(audio);
        if (stream) setAudioStream(stream);
      }
      void audio.play();
      setIsPlaying(true);
    }
  }, [audioStream, isPlaying, setAudioStream]);

  return (
    <div className="flex flex-col gap-1 w-full">
      {/* biome-ignore lint/a11y/useMediaCaption: This hidden audio element backs the custom playback controls; no caption track is provided by the recording. */}
      <audio
        ref={audioRef}
        src={mediaBlobUrl}
        style={{ visibility: "hidden", width: 0, height: 0 }}
      />
      <RecorderVisualizer
        controls
        audio={audioStream}
        paused={!isPlaying}
        onToggle={togglePlayPause}
      />
      <div className="text-center">
        <p className="font-medium">
          {formatTime(currentTime)} / {formatTime(duration)}
        </p>
      </div>
    </div>
  );
};

export default RecorderAudioPlayer;
